/**
 * pdfThumbnail — render the first page of a PDF to a JPEG blob URL.
 *
 * Used by `makeAttachedDoc` (captureRouting.ts) so wizard tiles and the
 * Documents tab show the actual document content as a thumbnail instead
 * of a generic 📄 icon. Bill 2026-05-03: "Can you not render the PDF
 * itself instead of the PDF Icon?"
 *
 * Implementation:
 *   - Lazy-imports `pdfjs-dist` so the ~1MB worker payload doesn't load
 *     on the critical path.
 *   - Loads the PDF from raw bytes (no fetch).
 *   - Renders page 1 to an off-screen canvas at a target width.
 *   - Encodes the canvas to a JPEG blob and wraps in `URL.createObjectURL`.
 *   - Returns the blob URL string. Caller is responsible for calling
 *     URL.revokeObjectURL() if/when the doc is replaced.
 *
 * Failure modes pass through silently — caller falls back to the doc
 * icon when this returns ''. We never want a missing thumb to break
 * the capture flow.
 */

import { loadPdfJs as getPdfjs } from '@/services/pdfjs';

/**
 * Decode a base64 string to a fresh Uint8Array. We pass the bytes to
 * pdf.js directly — no fetch round-trip, which is critical for offline /
 * Teams iframe contexts where data: URLs sometimes get blocked.
 */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export interface PdfThumbnailOptions {
  /** Target width in CSS pixels. Default 240. */
  width?: number;
  /** JPEG encoder quality 0..1. Default 0.85. */
  quality?: number;
}

/**
 * Render page 1 of a base64 PDF to a JPEG blob: URL.
 * Returns '' on any failure (worker init, invalid PDF, encode error).
 */
export async function pdfFirstPageThumbnailFromBase64(
  base64: string,
  opts: PdfThumbnailOptions = {},
): Promise<string> {
  const width = opts.width ?? 240;
  const quality = opts.quality ?? 0.85;
  let pdfDoc: import('pdfjs-dist').PDFDocumentProxy | null = null;
  try {
    const pdfjs = await getPdfjs();
    const bytes = base64ToBytes(base64);
    // Pass a fresh copy of the buffer — pdf.js may transfer ownership.
    const loadingTask = pdfjs.getDocument({ data: bytes.slice() });
    pdfDoc = await loadingTask.promise;
    const page = await pdfDoc.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = width / baseViewport.width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
    });
    if (!blob) return '';
    return URL.createObjectURL(blob);
  } catch {
    return '';
  } finally {
    try { await pdfDoc?.cleanup(); } catch { /* ignore */ }
    try { await pdfDoc?.destroy(); } catch { /* ignore */ }
  }
}
